<script setup lang="ts">
/**
 * The sidebar (design prototype `#sidebar`, `renderNav`): the brand with "powered by" (its
 * wordmark in the text colour too, as in the prototype), who is here (B-03, C-06), the navigation
 * the session allows (src/router/navigation.ts) with the counters of the staff's orders and work
 * lists (C-08), and, for guests, the "Schon Kunde?" block with the way to the application page
 * `/beta` (ADR-143 addendum 3; in the alpha release in place of "Zugang anfragen", TL 2026-10-27;
 * on phones the header has no room for it); a guest reads "Alle KI-Produkte an einem Ort" only
 * while the guest view is on (ADR-133 point 3). No item leads to a product's tools any more:
 * "aiCockpit öffnen" stands on the customer's overview and under "Berechtigungen" (ADR-150
 * point 2). Always dark like the prototype (`data-scheme="dark"` flips the tokens inside). From
 * md up it stays in place; below it is a drawer with a backdrop that closes on Escape, on the
 * backdrop, after navigating, and when the window grows to md (a tablet turned, PF2), where
 * neither backdrop nor close button stand. The drawer leads the focus (GU4): opened, to its close
 * button; closed, once the page behind is no longer inert (`App.vue`, PF1), back to where it was
 * before, else to the header's menu button (`opener`; Safari focuses no button on a click, PF9).
 * A navigation item says that it is current with
 * `aria-current`, `page` on its route and `true` on a child route such as a customer's file under
 * "Kunden" (review finding 6), and `true` on the family's item on a product's page (`familyItem`,
 * an agent's page under "feratel Agenten", ADR-150 point 4); the active look follows the attribute.
 * Every item carries the prototype's icon (`src/lib/nav-icons.ts`), its tinted area visible only on
 * the current item, as in the prototype; the products' items carry their marks.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, useTemplateRef, watch } from "vue";
import { useRouter } from "vue-router";
import type { SessionResponse } from "@ai-platform/contracts";
import PixButton from "@pixelpoint-pattern-library-v2/base/components/styled/base/primitives/PixButton/PixButton.vue";
import BrandLogo from "./BrandLogo.vue";
import type { NavCount } from "../composables/navigation.ts";
import type { NavGroup } from "../router/navigation.ts";
import { MD_UP_QUERY } from "../lib/breakpoints.ts";
import { navIcons, navMarks } from "../lib/nav-icons.ts";

const props = withDefaults(
  defineProps<{
    open: boolean;
    groups: NavGroup[];
    isGuest: boolean;
    /** guests see the catalogue (`PortalSettings.guestCatalog`) */
    guestCatalog?: boolean;
    session?: SessionResponse | null;
    counts?: Partial<Record<string, NavCount>>;
    /** the route name of the family's item that stays current on a product's page (`useProductFamilyItem`) */
    familyItem?: string | null;
    /** the button that opens the drawer; the focus goes back there when nothing else can take it */
    opener?: HTMLElement | null;
  }>(),
  { guestCatalog: true, session: null, counts: () => ({}), familyItem: null, opener: null },
);

const emit = defineEmits<{ close: []; login: [] }>();

const router = useRouter();

/** Who is here, from the session; nothing while it is not known yet. */
const who = computed(() => {
  const { session } = props;
  if (session?.customer) return { label: "Kunde", name: session.customer.name, detail: session.customer.instanceHost };
  if (session?.staffRole) return { label: "Team", name: "pixelpoint dataCycle", detail: null };
  if (props.isGuest) {
    return {
      label: "Nicht angemeldet",
      name: props.guestCatalog ? "Alle KI-Produkte an einem Ort" : null,
      detail: null,
    };
  }
  return null;
});

const linkClass =
  "group rounded-control text-ui text-sidebar-item hover:bg-sidebar-hover hover:text-text-head focus-visible:outline-focus [&[aria-current]]:bg-sidebar-active-bg [&[aria-current]]:text-text-head flex min-h-(--size-field) items-center gap-3 px-3 font-medium no-underline focus-visible:outline-2 focus-visible:outline-offset-2 [&[aria-current]]:font-semibold [&[aria-current]]:shadow-sidebar-active";

/**
 * `page` on the item's own route, `true` on one of its child routes and on its family's product
 * page, nothing elsewhere.
 */
function currentOf(name: string, isActive: boolean, isExactActive: boolean): "page" | "true" | undefined {
  if (isExactActive) return "page";
  return isActive || name === props.familyItem ? "true" : undefined;
}

/** Follows a navigation item (modifier keys stay with the browser) and closes the drawer. */
function follow(event: MouseEvent, navigate: (event?: MouseEvent) => unknown) {
  void navigate(event);
  emit("close");
}

/** To the application page, a button like "Anmelden" beside it; the drawer closes. */
function apply() {
  emit("close");
  void router.push({ name: "application" });
}

function onKeyDown(event: KeyboardEvent) {
  if (event.key === "Escape" && props.open) emit("close");
}

const aside = useTemplateRef<HTMLElement>("aside");
const closeButton = useTemplateRef<HTMLButtonElement>("closeButton");
/** What had the focus before the drawer opened, to get it back when it closes; never `body`. */
let returnFocus: HTMLElement | null = null;

/** Whether the focus is lost or still in the closing drawer, so giving it back takes nothing away. */
function focusLeftBehind(): boolean {
  const active = document.activeElement;
  return !active || active === document.body || aside.value?.contains(active) === true;
}

/**
 * Gives the focus back after the drawer closed: to what had it before, else to the opener. Only
 * after the next render, when `App.vue` has taken `inert` from the page: `focus()` in an inert
 * subtree does nothing (PF1).
 */
async function giveFocusBack() {
  const target = returnFocus;
  returnFocus = null;
  await nextTick();
  if (!focusLeftBehind()) return;
  if (target?.isConnected) target.focus();
  if (document.activeElement !== target) props.opener?.focus();
}

watch(
  () => props.open,
  async (open) => {
    if (!open) {
      await giveFocusBack();
      return;
    }
    const active = document.activeElement;
    returnFocus = active instanceof HTMLElement && active !== document.body ? active : null;
    await nextTick();
    closeButton.value?.focus();
  },
);

/** From md up there is no drawer, so an open one closes when the window grows (PF2). */
let mdUp: MediaQueryList | null = null;
function onMdUp(event: MediaQueryListEvent) {
  if (event.matches && props.open) emit("close");
}

onMounted(() => {
  document.addEventListener("keydown", onKeyDown);
  if (typeof window.matchMedia !== "function") return;
  mdUp = window.matchMedia(MD_UP_QUERY);
  mdUp.addEventListener("change", onMdUp);
});
onBeforeUnmount(() => {
  document.removeEventListener("keydown", onKeyDown);
  mdUp?.removeEventListener("change", onMdUp);
});
</script>

<template>
  <div
    v-if="props.open"
    class="bg-backdrop fixed inset-0 z-(--z-modal-backdrop) md:hidden"
    aria-hidden="true"
    data-drawer-backdrop
    @click="emit('close')"
  />
  <aside
    ref="aside"
    data-scheme="dark"
    :data-open="props.open || undefined"
    class="bg-sidebar-solid text-text-body ease-brand grain border-sidebar-line md:bg-sidebar fixed inset-y-0 left-0 z-(--z-modal) flex w-(--size-sidebar) -translate-x-full flex-col border-r transition-transform duration-(--duration-slide) data-open:translate-x-0 motion-reduce:transition-none max-md:invisible max-md:data-open:visible md:z-(--z-sticky) md:translate-x-0"
    aria-label="Seitenleiste"
  >
    <div class="border-sidebar-line flex h-(--size-header) flex-none items-center justify-between gap-2 border-b px-4">
      <RouterLink
        :to="{ name: 'home' }"
        class="rounded-chip text-text-head focus-visible:outline-focus inline-flex items-center focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        <BrandLogo class="h-17 [--color-accent-text:currentColor]" />
      </RouterLink>
      <button
        ref="closeButton"
        type="button"
        class="rounded-pill text-text-soft hover:bg-fill hover:text-text-head focus-visible:outline-focus inline-flex size-(--size-control) cursor-pointer items-center justify-center border-0 bg-transparent focus-visible:outline-2 focus-visible:outline-offset-2 md:hidden"
        aria-label="Menü schließen"
        @click="emit('close')"
      >
        <svg class="size-4.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M6 6l12 12M18 6 6 18" stroke-linecap="round" />
        </svg>
      </button>
    </div>
    <div v-if="who" class="border-sidebar-line flex-none border-b px-5 py-4" data-who>
      <p class="text-meta text-text-soft m-0">{{ who.label }}</p>
      <p v-if="who.name" class="text-ui text-text-head m-0 mt-0.5 truncate font-semibold">{{ who.name }}</p>
      <p v-if="who.detail" class="text-meta text-text-faint m-0 truncate">{{ who.detail }}</p>
    </div>

    <nav class="flex-1 overflow-y-auto px-3 pt-5 pb-2" aria-label="Hauptnavigation">
      <div v-for="group in props.groups" :key="group.id" class="mb-5">
        <p v-if="group.title" class="text-meta text-text-soft mx-3 mt-0 mb-2 font-semibold">
          {{ group.title }}
        </p>
        <ul class="m-0 flex list-none flex-col gap-0.5 p-0">
          <li v-for="item in group.items" :key="item.name">
            <RouterLink v-slot="{ href, navigate, isActive, isExactActive }" :to="{ name: item.name }" custom>
              <a
                :href="href"
                :class="linkClass"
                :aria-current="currentOf(item.name, isActive, isExactActive)"
                @click="follow($event, navigate)"
              >
                <component
                  :is="navMarks[item.name]"
                  v-if="navMarks[item.name]"
                  class="size-4.75 flex-none"
                  data-nav-icon
                />
                <svg
                  v-else-if="navIcons[item.name]"
                  class="size-4.5 flex-none"
                  viewBox="0 0 256 256"
                  fill="currentColor"
                  aria-hidden="true"
                  data-nav-icon
                >
                  <!-- the tint shows on the current item only, in the sidebar's active blue -->
                  <path
                    :d="navIcons[item.name]?.tint"
                    class="group-[[aria-current]]:fill-sidebar-active opacity-0 group-[[aria-current]]:opacity-50"
                  />
                  <path :d="navIcons[item.name]?.shape" />
                </svg>
                {{ item.title }}
                <template v-if="props.counts[item.name]">
                  <span
                    class="rounded-pill bg-sidebar-count text-meta text-on-sidebar-count ml-auto inline-flex h-6 min-w-6 items-center justify-center px-1.5 font-bold tabular-nums"
                    aria-hidden="true"
                    :data-count="item.name"
                  >
                    {{ props.counts[item.name]?.count }}
                  </span>
                  <span class="sr-only">, {{ props.counts[item.name]?.label }}</span>
                </template>
              </a>
            </RouterLink>
          </li>
        </ul>
      </div>
    </nav>

    <div v-if="props.isGuest" class="rounded-card bg-sidebar-panel m-3 p-4">
      <p class="text-text-head m-0 font-semibold">Schon Kunde?</p>
      <p class="text-meta text-text-soft mt-1 mb-3">Mit eurem dataCycle-Konto anmelden.</p>
      <PixButton class="text-ui min-h-(--size-field) w-full font-semibold" @click="emit('login')">Anmelden</PixButton>
      <p class="text-text-head mt-4 mb-0 font-semibold">Noch kein Zugang?</p>
      <p class="text-meta text-text-soft mt-1 mb-3">Erzählt uns, was ihr vorhabt.</p>
      <PixButton outline class="text-ui min-h-(--size-field) w-full font-semibold" data-action="apply" @click="apply">
        Für die Beta bewerben
      </PixButton>
    </div>
  </aside>
</template>
